import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { adminClient, type StudioTemplateListFilter, type StudioTemplatePage } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { DataTable, DataTableCell, DataTableHead, DataTableLoadError, DataTableMessage, DataTableRow } from '@/components/table/DataTable';
import { SearchInput } from '@/components/table/SearchInput';
import { SegmentedFilter } from '@/components/table/SegmentedFilter';
import { TablePager } from '@/components/table/TablePager';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

import { errorMessage, relativeTime } from '../IntegrationBuilder/builderModel';
import {
  TEMPLATE_EMPTY_LABELS,
  TEMPLATE_FILTERS,
  TEMPLATE_PAGE_SIZE,
  TEMPLATE_STATUS_META,
  type TemplateListState,
  templateListParams,
  templateListStateFrom,
} from './templateModel';

const COLUMNS = [
  { label: 'Template' },
  { label: 'Status' },
  { label: 'Live version', className: 'hidden md:table-cell' },
  { label: 'Used', className: 'hidden md:table-cell' },
  { label: 'Updated', className: 'text-right' },
];

/**
 * Studio Admin → Templates: paged and searched on the server. Search, filter and
 * page live in the address. With no filter chosen it opens on the review queue,
 * or on every template when nothing is waiting.
 */
export function TemplatesListPage() {
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => templateListStateFrom(params), [params]);
  const [search, setSearch] = useState(state.q);
  const debounced = useDebouncedValue(search.trim());
  const [queueEmpty, setQueueEmpty] = useState(false);
  const [result, setResult] = useState<StudioTemplatePage>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const request = useRef(0);

  const status: StudioTemplateListFilter = state.status ?? (queueEmpty ? 'all' : 'review');
  const offset = (state.page - 1) * TEMPLATE_PAGE_SIZE;

  const update = useCallback((next: TemplateListState) => setParams(templateListParams(next), { replace: true }), [setParams]);

  const lastSearch = useRef(debounced);
  useEffect(() => {
    if (lastSearch.current === debounced) return;
    lastSearch.current = debounced;
    if (debounced !== state.q) update({ ...state, q: debounced, page: 1 });
  }, [debounced, state, update]);

  const load = useCallback(async () => {
    const id = ++request.current;
    setLoading(true);
    setError(undefined);
    try {
      const page = await adminClient.listStudioTemplates({ q: state.q || undefined, status, limit: TEMPLATE_PAGE_SIZE, offset });
      if (id !== request.current) return;
      setResult(page);
      if (!state.status && !state.q && page.counts.review === 0) setQueueEmpty(true);
      if (page.items.length === 0 && page.total > 0 && offset > 0) {
        update({ ...state, page: Math.ceil(page.total / TEMPLATE_PAGE_SIZE) });
      }
    } catch (failure) {
      if (id === request.current) setError(errorMessage(failure));
    } finally {
      if (id === request.current) setLoading(false);
    }
  }, [state, status, offset, update]);

  useEffect(() => {
    void load();
  }, [load]);

  const items = result?.items ?? [];

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader
        title="Templates"
        description="Apps RapidSOS built in Studio that customers can start from. Review what authors submit, then publish it to the gallery."
      />

      <DataTable
        toolbar={
          <>
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search title, slug, description or id"
              label="Search templates"
            />
            <SegmentedFilter<StudioTemplateListFilter>
              label="Show"
              value={status}
              onChange={(next) => update({ ...state, status: next, page: 1 })}
              options={TEMPLATE_FILTERS.map((option) => ({ ...option, count: result?.counts[option.id] }))}
            />
          </>
        }
        footer={
          result && !error ? (
            <TablePager
              total={result.total}
              limit={TEMPLATE_PAGE_SIZE}
              offset={offset}
              disabled={loading}
              onOffsetChange={(next) => update({ ...state, page: Math.floor(next / TEMPLATE_PAGE_SIZE) + 1 })}
            />
          ) : null
        }
      >
        <DataTableHead columns={COLUMNS} />
        {loading && !result ? (
          <DataTableMessage colSpan={COLUMNS.length} loading>
            Loading templates
          </DataTableMessage>
        ) : error ? (
          <DataTableLoadError colSpan={COLUMNS.length} message={error} onRetry={() => void load()} />
        ) : items.length === 0 ? (
          <DataTableMessage colSpan={COLUMNS.length}>
            {state.q ? (
              `No templates match “${state.q}”`
            ) : (
              <>
                <span className="block font-medium text-foreground">{TEMPLATE_EMPTY_LABELS[status]}</span>
                <span className="mx-auto mt-1 block max-w-sm text-xs leading-5">
                  A RapidSOS owner or admin submits an app from Studio with ⋮ → Submit as template. It shows up here for review.
                </span>
              </>
            )}
          </DataTableMessage>
        ) : (
          <tbody className={loading ? 'opacity-60' : undefined}>
            {items.map((template) => {
              const to = `/templates/${template.templateId}`;
              return (
                <DataTableRow key={template.templateId} to={to}>
                  <DataTableCell className="max-w-md">
                    <Link to={to} className="block min-w-0">
                      <span className="block truncate font-medium">{template.title}</span>
                      <span className="block truncate text-[11px] text-muted-foreground">
                        {template.audience === 'internal' ? 'Internal · ' : ''}
                        {template.category}
                        {template.summary ? ` · ${template.summary}` : ''}
                      </span>
                    </Link>
                  </DataTableCell>
                  <DataTableCell>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <StatusPill tone={TEMPLATE_STATUS_META[template.status].tone}>{TEMPLATE_STATUS_META[template.status].label}</StatusPill>
                      {template.pendingVersion ? (
                        <StatusPill tone="active">v{template.pendingVersion.version} to review</StatusPill>
                      ) : null}
                    </div>
                  </DataTableCell>
                  <DataTableCell className="hidden font-mono text-xs md:table-cell">
                    {template.latestPublishedVersion ? `v${template.latestPublishedVersion}` : '—'}
                  </DataTableCell>
                  <DataTableCell className="hidden font-mono text-xs md:table-cell">{template.usageCount || '—'}</DataTableCell>
                  <DataTableCell className="text-right text-xs text-muted-foreground">
                    {relativeTime(template.updatedAt)}
                    <div className="truncate text-[11px]">{template.updatedBy}</div>
                  </DataTableCell>
                </DataTableRow>
              );
            })}
          </tbody>
        )}
      </DataTable>
    </div>
  );
}
